export const boqArticles = [
    {
        id: 'boq.overview',
        title: 'BOQ Overview',
        category: 'BOQ',
        order: 1,
        summary: 'Understanding the Bill of Quantities page and its features',
        content: `
The Bill of Quantities (BOQ) page is where you build and manage detailed cost estimates for jobs. It combines catalogue items, adhoc entries, and supplier pricing to create comprehensive project estimates.

**Page Layout:**

- **Toolbar** - Job selector, price level, and action buttons
- **Cost Centre Panel** - Hierarchical organization of items (left side)
- **BOQ Grid** - Line items with quantities and pricing (main area)
- **Catalogue Search** - Search and add items from the catalogue (right panel)

**Key Features:**

- Add items from the master catalogue
- Create custom adhoc items
- Assign suppliers to items
- Calculate totals using 17 different calculation routines
- Copy a BOQ (or parts of it) from another job or template
- Import quantities from external files
- Send items to zzTakeoff for measurement
- Drag and drop items between cost centres
    `,
        expandableSections: [
            {
                title: 'Selecting a Job',
                content: `
Before working with the BOQ, you must select a job:

1. Use the **Job** dropdown in the toolbar
2. Type to search for jobs by number or name
3. Select the job from the dropdown
4. The BOQ grid will load with that job's items

**Tip:** Recently accessed jobs appear at the top of the list.
        `,
            },
            {
                title: 'Price Levels',
                content: `
The **Level** dropdown controls which price level is used:

- **Level 1** - Estimating price (default)
- **Level 2** - Regional pricing
- **Level 3** - Sales pricing
- **Level 4-5** - Custom price levels

Changing the price level updates all catalogue item prices. Supplier-specific prices override the selected level.
        `,
            },
            {
                title: 'Bill Date',
                content: `
The **Date** field determines which prices are used:

- Prices are date-effective
- The system uses prices valid as of this date
- Useful for historical quotes or future projections
        `,
            },
        ],
        keywords: ['boq', 'bill of quantities', 'estimate', 'pricing', 'quantities'],
        route: '/boq',
        relatedArticles: ['boq.add-item', 'boq.cost-centres', 'boq.suppliers', 'features.calculations'],
        screenshots: [
            {
                src: 'boq/boq-grid-editing.png',
                alt: 'Inline grid editing',
                caption: 'Edit quantities and prices directly in the grid',
            },
            {
                src: 'boq/boq-context-menu.png',
                alt: 'Context menu',
                caption: 'Right-click for quick access to common actions',
            },
        ],
    },
    {
        id: 'boq.add-item',
        title: 'Adding Items from Catalogue',
        category: 'BOQ',
        order: 2,
        summary: 'How to search and add catalogue items to your BOQ',
        content: `
The catalogue search allows you to find and add pre-defined items to your BOQ. Items come with default pricing, units, and calculation settings.

**Opening Catalogue Search:**

- Click the **Catalogue** button in the toolbar
- Or press **Ctrl+K** to toggle the search panel

**Searching:**

- Type in the search box to find items
- Search matches on price code, description, or keywords
- Results appear in a dropdown list
    `,
        expandableSections: [
            {
                title: 'Adding Items',
                content: `
**Quick Add:**
1. Open the catalogue search
2. Type to search for items
3. Click an item to add it to the BOQ
4. The item is added under the currently selected cost centre

**With Quantity:**
1. Search for the item
2. Enter a quantity in the search input
3. Press Enter to add with that quantity

**Multiple Items:**
- Keep searching and clicking to add multiple items
- Each item is added to the selected cost centre
        `,
            },
            {
                title: 'Item Properties',
                content: `
When you add a catalogue item, it inherits:

- **Price Code** - Unique catalogue identifier
- **Description** - Item description
- **Unit** - Unit of measure (with calculation routine)
- **Unit Price** - From the selected price level
- **Cost Centre** - From your current selection

You can modify these values after adding the item.
        `,
            },
        ],
        keywords: ['add item', 'catalogue search', 'search', 'add from catalogue'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'boq.adhoc', 'catalogue.overview'],
        screenshots: [
            {
                src: 'boq/boq-add-item-modal.png',
                alt: 'Add item from catalogue',
                caption: 'Search and add items from your catalogue to the BOQ',
            },
        ],
    },
    {
        id: 'boq.adhoc',
        title: 'Creating Adhoc Items',
        category: 'BOQ',
        order: 3,
        summary: 'How to create custom line items not in the catalogue',
        content: `
Adhoc items are custom line items that don't exist in your catalogue. Use them for one-off items, special conditions, or job-specific requirements.

**When to Use Adhoc:**

- Items not in the master catalogue
- One-time special items
- Allowances and contingencies
- Job-specific adjustments
    `,
        expandableSections: [
            {
                title: 'Creating an Adhoc Item',
                content: `
1. Click the **Adhoc** button in the toolbar
2. Fill in the required fields:
   - **Description** - What the item is
   - **Unit** - Select a unit of measure
   - **Quantity** - How many/much
   - **Unit Price** - Price per unit
3. Optionally set:
   - **Cost Centre** - Where it belongs
   - **Supplier** - If already known
4. Click **Add** to create the item

The item appears in the BOQ grid under the selected cost centre.
        `,
            },
            {
                title: 'Adhoc vs Catalogue Items',
                content: `
**Adhoc Items:**
- Created directly in the BOQ
- Exist only in this job
- Won't sync with catalogue price changes
- Marked with an indicator in the grid

**Catalogue Items:**
- Linked to master catalogue
- Can be repriced from catalogue
- Shared across jobs
- Maintain price history
        `,
            },
        ],
        keywords: ['adhoc', 'custom item', 'one-off', 'create item', 'manual entry'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'boq.add-item'],
        screenshots: [
            {
                src: 'boq/boq-adhoc-modal.png',
                alt: 'Add adhoc item',
                caption: 'Create custom items not in the catalogue',
            },
        ],
    },
    {
        id: 'boq.cost-centres',
        title: 'Working with Cost Centres',
        category: 'BOQ',
        order: 4,
        summary: 'Organizing BOQ items into cost centres and subgroups',
        content: `
Cost centres organise your BOQ into the sections you estimate and order by, grouped under **trade groups** (e.g. Pre-Construction, Base, Framing, Lockup).

**Cost Centre Panel:**

The left panel shows trade groups, their cost centres, and each cost centre's loads:

- Expand/collapse a trade group with its arrow, or use the expand-all / collapse-all buttons in the header
- Click a cost centre to filter the grid to it; click **All Items** to clear the filter
- Expand a cost centre to see its **loads** — click a load to filter to just that load, or raise a draft purchase order straight from it
- Reorder trade groups with the **Move Up / Move Down** arrows on the group row
- The search box finds a section by code or name; the **Active** toggle hides sections with no items
    `,
        expandableSections: [
            {
                title: 'Adding Cost Centres',
                content: `
Cost centres are managed centrally — they're shared by every job, so the BOQ panel doesn't create them:

1. Go to **Settings → Cost Centres**
2. Add the code, name and trade group
3. The cost centre is then available on every job's BOQ

**Hierarchy:** trade group → cost centre → loads. Each load within a cost centre becomes its own purchase order.
        `,
            },
            {
                title: 'Assigning Items',
                content: `
**When Adding Items:**
- Select a cost centre in the panel first
- New items are automatically assigned to it

**Moving Existing Items:**
1. Select items in the grid using the drag handle (grip icon)
2. Drag towards the cost centre panel on the left
3. Click on the target cost centre to drop items there
4. Items are moved with proper load number handling

**Smart Load Assignment:**
When moving items to a cost centre:
- If the item's current load is already ordered, it gets assigned to the next available load
- Items on ordered purchase orders cannot be moved
- A message shows how many items were reassigned to new loads

**Bulk Assignment:**
1. Select multiple items
2. Right-click and choose "Assign Cost Centre"
3. Select the target cost centre
        `,
            },
            {
                title: 'Cost Centre Totals',
                content: `
Each cost centre shows running totals:

- **Item Count** - Number of items in the section
- **Subtotal** - Sum of all item values

Click **All Items** at the top of the panel to see every cost centre's items in the grid; the footer shows the count and total for the current selection.
        `,
            },
        ],
        keywords: ['cost centre', 'cost center', 'sections', 'hierarchy', 'organization'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'catalogue.cost-centres', 'settings.cost-centres'],
        screenshots: [
            {
                src: 'boq/boq-cost-centres.png',
                alt: 'Cost centre panel with trade groups and totals',
                caption: 'Trade groups with their cost centres and running totals — click a section to filter the grid',
            },
        ],
    },
    {
        id: 'boq.suppliers',
        title: 'Assigning Suppliers',
        category: 'BOQ',
        order: 5,
        summary: 'How to assign suppliers to BOQ items for pricing and ordering',
        content: `
Assigning suppliers to BOQ items allows you to use supplier-specific pricing and prepare items for purchase orders.

**Why Assign Suppliers:**

- Use negotiated supplier pricing
- Prepare items for ordering
- Track supplier commitments
- Compare supplier quotes
    `,
        expandableSections: [
            {
                title: 'Assigning a Supplier',
                content: `
**Single Item:**
1. Click the Supplier column cell for an item
2. Type to search for suppliers
3. Select the supplier from the dropdown
4. The price updates if supplier-specific pricing exists

**Multiple Items:**
1. Select multiple items in the grid
2. Click the **Assign Supplier** button in the toolbar
3. Search and select the supplier
4. All selected items are assigned

**From Context Menu:**
1. Right-click selected items
2. Choose "Assign Supplier"
3. Select the supplier
        `,
            },
            {
                title: 'Supplier Pricing',
                content: `
When a supplier is assigned:

1. System checks for supplier-specific price
2. If found, uses supplier price
3. If not found, keeps catalogue price

**Price Hierarchy:**
1. **Supplier Price** (if assigned and exists)
2. **BOQ Unit Price** (from original entry)
3. **Catalogue Price** (from selected level)
        `,
            },
            {
                title: 'Locked Items',
                content: `
Items with purchase orders are "locked":

- Cannot change supplier
- Cannot delete the item
- Cannot modify key fields
- Shown with a lock icon

To unlock, the associated order must be cancelled.
        `,
            },
        ],
        keywords: ['supplier', 'assign supplier', 'pricing', 'supplier price', 'nominated'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'suppliers.overview', 'orders.create'],
        screenshots: [
            {
                src: 'boq/boq-assign-supplier.png',
                alt: 'Assign supplier',
                caption: 'Assign suppliers to BOQ items for pricing and ordering',
            },
            {
                src: 'boq/ordered-bill-items.png',
                alt: 'Ordered bill items',
                caption: 'Items with purchase orders are locked and show order details',
            },
        ],
    },
    {
        id: 'boq.copy-from-job',
        title: 'Copy BOQ from Another Job',
        category: 'BOQ',
        order: 11,
        summary: 'Copy a full or partial bill from another job or a template job',
        content: `
Use **Copy from** in the BOQ toolbar to bring another job's bill into the current job — the classic "standard house template" workflow.

**How it works:**

1. Open the destination job's BOQ and click **Copy from**
2. Pick the source job — the list can be filtered by job type/status, and **Template** jobs are included even though they're hidden from the Jobs list
3. Tick the cost centres and items you want (everything starts selected)
4. Choose the pricing and quantity options
5. Click **Copy**

Copied lines are **appended** — nothing already in the destination job is changed. Line numbers continue after the existing lines in each cost centre.
    `,
        expandableSections: [
            {
                title: 'Template Jobs',
                content: `
Keep your standard-house bills as jobs with status **Template**:

- Template jobs are hidden from the main Jobs list, so they don't clutter day-to-day work
- They stay selectable in the Copy from picker
- The picker's job-type filter is remembered, so if you always copy from Templates it opens ready to go
        `,
            },
            {
                title: 'Choosing What to Copy',
                content: `
The picker shows the source bill grouped by cost centre:

- Tick or untick whole cost centres, or expand one and pick individual items
- A **has N · appends** badge marks cost centres where the destination job already has lines — expanding one shows those existing lines (greyed) so you can see exactly what you'd be appending after
- The running total updates as you change the selection
        `,
            },
            {
                title: 'Pricing and Quantity Options',
                content: `
**Pricing:**

- **Use current estimate prices** (default) — every line takes today's level-1 catalogue price; the template's structure carries over but the pricing is fresh
- **Keep the source job's prices** — copies prices exactly as they are on the source job

**Zero out the quantities** — copies the items and rates only; every line lands with quantity 0 so quantities can be measured for this job. Quantity formulas are cleared too.

**Copy allocated suppliers** (default on) — untick to land every line unassigned, ready for fresh supplier allocation.

**Copy notes & workups** (default on) — brings across each line's workup/notes text AND each copied load's **order notes** (including standard notes already inserted on the source order), so nothing has to be re-selected on the new drafts. Untick to start clean. Pre-existing target orders keep their own notes.
        `,
            },
            {
                title: 'What Carries Over',
                content: `
**Copied:** quantities (unless zeroed), formulas, workup notes and supplier assignments (both optional — see Pricing and Quantity Options), assembly overrides, unit overrides, headings, and allowances (PS/PC allocations are re-linked within the new job).

**Never copied:** order status — every copied line lands unordered, and draft order shells are created per copied load, ready to raise.

**Skipped:** loads that are already on a purchase order in the destination job are locked and skipped; the copy tells you which ones.

Works for migrated (Databuild) jobs too — the copy reads the same effective bill you see on screen.
        `,
            },
        ],
        screenshots: [
            {
                src: 'boq/copy-from-picker.png',
                alt: 'Copy BOQ job picker with the job-type filter',
                caption: 'Pick the source job — filter by job type (remembered for next time); Template jobs are included',
            },
            {
                src: 'boq/copy-from-tree.png',
                alt: 'Copy BOQ cost centre tree with append badges and existing lines',
                caption: "Tick what to copy. 'has N · appends' marks cost centres where this job already has lines — expand to see them above the incoming items",
            },
        ],
        relatedArticles: ['boq.overview', 'boq.add-item', 'jobs.overview'],
    },
    {
        id: 'boq.import',
        title: 'Importing Quantities',
        category: 'BOQ',
        order: 6,
        summary: 'How to import quantities from external files',
        content: `
The Import Quantities feature allows you to update BOQ quantities from external files such as Excel spreadsheets or CSV files.

**Supported Formats:**

- Excel (.xlsx, .xls)
- CSV (.csv)
- Tab-delimited text (.txt)

**Use Cases:**

- Import quantities from takeoff software
- Bulk update quantities from site measurements
- Sync with external estimating systems
    `,
        expandableSections: [
            {
                title: 'Import Process',
                content: `
1. Click **Import** button in the toolbar
2. Select or drag your file
3. Map columns to BOQ fields:
   - Price Code (required for matching)
   - Quantity (the value to import)
4. Preview the import
5. Click **Import** to apply changes

**Matching:**
Items are matched by Price Code. Unmatched items can be:
- Added as new items
- Skipped
- Flagged for review
        `,
            },
            {
                title: 'File Format',
                content: `
Your import file should contain:

| Column | Description |
|--------|-------------|
| Price Code | Matches to catalogue/BOQ items |
| Quantity | The new quantity value |
| Description | (Optional) For verification |

**Tips:**
- Include a header row
- Use consistent price codes
- Remove any non-data rows (totals, headers)
        `,
            },
        ],
        keywords: ['import', 'quantities', 'excel', 'csv', 'upload', 'bulk update'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'boq.add-item'],
    },
    {
        id: 'boq.reprice',
        title: 'Repricing the Bill',
        category: 'BOQ',
        order: 7,
        summary: 'How to update BOQ prices from the catalogue',
        content: `
The Reprice function updates BOQ item prices from the current catalogue prices. Use this when catalogue prices have changed and you want to reflect the latest pricing.

**What Gets Updated:**

- Catalogue items get their prices updated
- Uses the selected price level
- Applies effective date pricing
- Respects supplier-specific prices
    `,
        expandableSections: [
            {
                title: 'Repricing Process',
                content: `
1. Select the job and verify settings:
   - Price Level
   - Bill Date
2. Click **Reprice** button in the toolbar
3. Review the confirmation:
   - Number of items affected
   - Items with price changes
4. Confirm to apply changes

**Exclusions:**
The following are NOT repriced:
- Adhoc items (not linked to catalogue)
- Items with orders (locked)
- Items with manual price overrides (optional)
        `,
            },
            {
                title: 'Price Change Report',
                content: `
After repricing, you can view a report showing:

- Items with price increases
- Items with price decreases
- Net change to bill total
- Items that couldn't be repriced

Use this to verify changes before proceeding.
        `,
            },
        ],
        keywords: ['reprice', 'update prices', 'refresh prices', 'price update'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'catalogue.prices'],
        screenshots: [
            {
                src: 'boq/boq-reprice-modal.png',
                alt: 'Reprice dialog',
                caption: 'Update BOQ prices from the current catalogue',
            },
        ],
    },
    {
        id: 'boq.export',
        title: 'Exporting the BOQ',
        category: 'BOQ',
        order: 8,
        summary: 'How to export BOQ data to Excel and other formats',
        content: `
Export your BOQ to Excel for reporting, sharing with clients, or further analysis.

**Export Options:**

- **Full Export** - All items with all columns
- **Summary Export** - Cost centre totals only
- **Selected Items** - Export only selected rows
    `,
        expandableSections: [
            {
                title: 'Export Process',
                content: `
1. Click **Export** button in the toolbar
2. Choose export format:
   - Excel (.xlsx)
   - CSV (.csv)
3. Select what to include:
   - All items or selected only
   - Which columns
   - Include/exclude hidden columns
4. Click **Export**

The file downloads automatically.
        `,
            },
            {
                title: 'Export Contents',
                content: `
The export typically includes:

| Column | Description |
|--------|-------------|
| Cost Centre | Section hierarchy |
| Price Code | Catalogue reference |
| Description | Item description |
| Unit | Unit of measure |
| Quantity | Amount |
| Unit Price | Price per unit |
| Value | Extended value |
| Supplier | Assigned supplier |

Customize columns using the Column Chooser before exporting.
        `,
            },
        ],
        keywords: ['export', 'excel', 'download', 'spreadsheet', 'report'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'reports.overview'],
    },
    {
        id: 'boq.zztakeoff',
        title: 'Send to zzTakeoff',
        category: 'BOQ',
        order: 9,
        summary: 'How to send BOQ items to zzTakeoff for quantity measurement',
        content: `
The zzTakeoff integration allows you to send BOQ items directly to zzTakeoff for measuring quantities from plans and drawings.

**What is zzTakeoff:**

zzTakeoff is a browser-based takeoff tool for measuring quantities from PDF plans. Items sent from Costed.app automatically configure the correct measurement type based on the unit of measure.

**Requirements:**

- zzTakeoff must be open in another browser tab
- Items must have a valid unit of measure
- Popup blockers should allow popups from this site
    `,
        expandableSections: [
            {
                title: 'Sending Items',
                content: `
**Single Item:**
1. Right-click an item in the BOQ grid
2. Select "Send to zzTakeoff"
3. zzTakeoff opens (or focuses) with the item configured
4. Measure the quantity on your plans

**Multiple Items:**
1. Select multiple items in the grid
2. Right-click to open context menu
3. Select "Send X Items to zzTakeoff"
4. Items are sent in sequence

**Keyboard Shortcut:**
- Select items and use the context menu
- Items are sent with their description, unit, and price
        `,
            },
            {
                title: 'Measurement Types',
                content: `
Items are automatically configured for the correct measurement type:

| Unit | Measurement Type |
|------|------------------|
| m2, sqm, sq m | Area measurement |
| m, lm, lin m | Linear measurement |
| m3, cu m | Volume/segment measurement |
| ea, no, set | Count measurement |

The measurement type is determined by the item's unit of measure.
        `,
            },
            {
                title: 'Troubleshooting',
                content: `
**Popup Blocked:**
If you see a popup blocked message:
1. Allow popups for this site in your browser
2. Try again

**zzTakeoff Not Opening:**
- Ensure zzTakeoff is accessible at zztakeoff.com
- Check your internet connection
- Try opening zzTakeoff manually first

**Items Not Appearing:**
- Verify the item has a valid unit
- Check that zzTakeoff is logged in and has a project selected
        `,
            },
        ],
        keywords: ['zztakeoff', 'takeoff', 'measurement', 'quantity takeoff', 'plans'],
        route: '/boq',
        relatedArticles: ['boq.overview', 'boq.add-item'],
    },
    {
        id: 'boq.assemblies',
        title: 'Assemblies on a BOQ',
        category: 'BOQ',
        order: 10,
        summary: 'Add, edit, explode and update assemblies (multi-item catalogue parts) on a bill',
        content: `
An **Assembly** is a catalogue item made up of several **Items** (for example a "2100 x 2400 Roller Door" assembly containing the door, tracks, fixings and labour). When you add an assembly to a BOQ it lands as a **single line**, priced as the sum of its Items.

Assembly lines are flagged with a blue **stack icon** in the Description column so you can spot unexploded assemblies at a glance, and the **Assemblies** toolbar toggle filters the bill to show only assembly lines.
    `,
        expandableSections: [
            {
                title: 'Spotting & filtering assemblies',
                content: `
- An unexploded assembly line shows a blue **stack icon** with the tooltip "Assembly (unexploded)".
- An **"edited"** badge appears when the Items on that line have been changed from the saved catalogue assembly.
- Click **Assemblies** in the grid toolbar (top-right) to filter the bill to **only** assembly lines; click again to clear. (It is mutually exclusive with the Allowances filter.)
        `,
            },
            {
                title: 'Editing the Items on a line',
                content: `
Right-click an assembly line → **Edit Assembly Items…**. This edits a **copy** of the assembly **for this BOQ line only** — the saved catalogue assembly is not changed.

In the editor you can:
- **Add Item** — search the whole catalogue (tick **Assembly items only** to narrow to items already tagged as assembly Items).
- **Ad-hoc Item** — add a free-text item with its own price, unit and cost centre.
- **Amend** any Item's quantity or price, or **delete** it.
- **Rename** the assembly as shown on this line.

The line price recalculates live from the Items. Click **Save** to apply the edits to this line only.
        `,
            },
            {
                title: 'Save vs Update Assembly vs Save as new',
                content: `
The editor offers three ways to save:

- **Save** — keeps the edited Items on **this BOQ line only** (an override). The catalogue assembly is untouched.
- **Update Assembly** — writes the edited Items **back to the saved catalogue assembly**, changing it for **every job** that uses it (you'll be asked to confirm). The line is re-priced to match unless it has been ordered.
- **Save as new Assembly** — creates a **brand-new** catalogue assembly (auto-coded \`ASM-…\`) from the edited Items and points this line at it. The original assembly is unchanged.
        `,
            },
            {
                title: 'Exploding an assembly',
                content: `
Right-click an assembly line → **Explode Assembly…** to replace the single line with one line per Item. The explosion uses your **edited copy** if you've changed it, and each Item is routed to its own cost centre. Assemblies nested inside assemblies are exploded all the way down.
        `,
            },
            {
                title: 'Ordered lines are locked',
                content: `
If a load/line has been **ordered**, its assembly Items can't be edited and the line won't be re-priced — **Edit Assembly Items…** is hidden for ordered lines. (You can still **Save as new Assembly** from an ordered line; it just won't re-point the locked line.)
        `,
            },
        ],
        keywords: ['assembly', 'assemblies', 'recipe', 'explode', 'assembly items', 'update assembly', 'unexploded', 'edit assembly', 'sub-items'],
        route: '/boq',
        relatedArticles: ['boq.add-item', 'boq.adhoc', 'catalogue.recipes'],
    },
];
